import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, KeyRound } from "lucide-react";
import { ApiError } from "@/api/client";
import { adminApi } from "@/api/admin";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/dates";
import type { AgenziaNonAssegnata, CatalogoUtenze, UtenzaCatalogo, UtenzaEvento } from "@/types/admin";
import type { ProvaAccessoStato } from "@/types";
import { ChipAgenzie, FormAssegna, StatoUtenza, UtenzeFornitoreTabella } from "./UtenzeFornitoreTabella";

// Solo A2A ha un adattatore funzionante (backend: adapters/a2a.py `disponibile = True`;
// Iren e Sentra lo hanno a False e le loro rotte rispondono `fornitore_non_disponibile`).
// Il catalogo non espone il flag per fornitore, quindi la lista sta qui, esplicita.
const FORNITORI_NEL_CATALOGO = new Set(["A2A"]);

// Come lavora l'utenza: la modalità la sceglie l'admin alla creazione, poi non
// si cambia più dal profilo dell'agenzia.
const RIGA_MODALITA: Record<string, string> = {
  custodite: "Il portale conserva le credenziali",
  sessione_temporanea: "Sessione temporanea: accedi con il codice della tua app",
};

// Il backend rifiuta con un codice: qui diventa una frase che dice la causa e la
// via d'uscita (regole ux "error-clarity" / "error-recovery").
const MOTIVO_RIFIUTO: Record<string, string> = {
  username_esistente: "Esiste già un'utenza con questo nome utente",
  modalita_non_ammessa: "Questo fornitore non lavora in questa modalità.",
  fornitore_non_disponibile: "Il collegamento a questo fornitore non è ancora disponibile.",
};

function messaggioErrore(e: unknown): string {
  const codice = e instanceof ApiError ? e.detail : "";
  return MOTIVO_RIFIUTO[codice] ?? "Operazione non riuscita. Riprova tra poco.";
}

function SectionCard({ title, subtitle, actions, children }: {
  title: string; subtitle?: string; actions?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Poll dell'esito di una prova d'accesso: giro limitato, come `attendiProva` lato agenzia. */
async function attendiProva(jobId: string, vivo: () => boolean): Promise<ProvaAccessoStato> {
  const t0 = Date.now();
  for (;;) {
    const st = await adminApi.utenzeProva(jobId);
    if (st.stato !== "in_corso") return st;
    if (Date.now() - t0 > 90_000 || !vivo()) {
      return { ...st, stato: "transitorio", dettaglio: "la verifica non si è conclusa in tempo" };
    }
    await new Promise((r) => setTimeout(r, 1000));
  }
}

export function UtenzeFornitore() {
  const [cat, setCat] = useState<CatalogoUtenze | null>(null);
  const [eventi, setEventi] = useState<UtenzaEvento[]>([]);
  const [nonAssegnate, setNonAssegnate] = useState<AgenziaNonAssegnata[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const montato = useRef(true);
  useEffect(() => {
    montato.current = true;
    return () => { montato.current = false; };
  }, []);

  const carica = useCallback(async () => {
    try {
      setCat(await adminApi.utenzeCatalogo());
      setErrore(null);
    } catch {
      setErrore("Impossibile caricare il catalogo delle utenze.");
    }
    try {
      setEventi(await adminApi.utenzeEventi());
    } catch {
      // il registro è accessorio: la pagina resta usabile senza
    }
    try {
      setNonAssegnate(await adminApi.utenzeNonAssegnate());
    } catch {
      // l'avviso è accessorio: senza la lettura non si mostra nulla, la pagina regge
      setNonAssegnate([]);
    }
  }, []);
  useEffect(() => { void carica(); }, [carica]);

  /**
   * Avvia una prova, aspetta l'esito e ridisegna il catalogo. Restituisce sempre un
   * esito — anche quando la lettura non risponde: chi ha chiesto la prova deve poter
   * riabilitare i suoi comandi, non restare appeso a «Verifica in corso…».
   */
  const seguiProva = useCallback(async (jobId: string): Promise<ProvaAccessoStato> => {
    setEsito("Verifica in corso…");
    let st: ProvaAccessoStato;
    try {
      st = await attendiProva(jobId, () => montato.current);
    } catch {
      st = { job_id: jobId, stato: "transitorio",
             dettaglio: "La verifica non ha risposto: controlla lo stato dell'utenza nel catalogo e riprova" };
    }
    if (!montato.current) return st;
    setEsito(st.dettaglio || (st.stato === "ok" ? "Accesso riuscito." : "Verifica non riuscita."));
    await carica();
    return st;
  }, [carica]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Utenze fornitore</h2>
        <p className="text-sm text-muted-foreground">
          Con quali credenziali il portale entra da ogni fornitore, e per conto di chi.
        </p>
      </div>

      <AvvisoNonAssegnate agenzie={nonAssegnate} />

      {errore && <p className="text-sm text-destructive">{errore}</p>}
      {esito && (
        <p role="status" aria-live="polite"
           className="text-sm rounded-lg border border-border bg-card px-3 py-2 text-foreground">
          {esito}
        </p>
      )}

      {cat === null ? (
        !errore && <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : (
        <>
          {cat.fornitori.map((f) => (
            <SezioneFornitore
              key={f}
              fornitore={f}
              utenze={cat.utenze.filter((u) => u.fornitore === f)}
              onRicarica={carica}
              onProva={seguiProva}
            />
          ))}

          <TettoBrowser key={cat.impostazioni.sessioni_browser_max}
                        valore={cat.impostazioni.sessioni_browser_max} />
          <Registro eventi={eventi} />
        </>
      )}
    </div>
  );
}

/**
 * Avviso in testa alla pagina: sta qui e non riga per riga perché riguarda agenzie
 * che in tabella non compaiono affatto — non hanno nessuna utenza. Tono ambra e
 * icona, non solo colore (regola a11y "color-not-only").
 */
function AvvisoNonAssegnate({ agenzie }: { agenzie: AgenziaNonAssegnata[] }) {
  if (agenzie.length === 0) return null;
  const nomi = agenzie.map((a) => a.nome).join(", ");
  return (
    <div role="status"
         className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <p className="flex items-start gap-2 text-sm text-amber-800 min-w-0">
        <AlertTriangle size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
        <span>Agenzie senza utenza A2A: {nomi}. Le loro pratiche restano in coda.</span>
      </p>
      <Button size="sm" variant="outline" className="sm:ml-auto shrink-0"
              onClick={() => document.getElementById("utenze-A2A")?.scrollIntoView({ block: "start" })}>
        Assegna
      </Button>
    </div>
  );
}

function SezioneFornitore({ fornitore, utenze, onRicarica, onProva }: {
  fornitore: string;
  utenze: UtenzaCatalogo[];
  onRicarica: () => Promise<void>;
  onProva: (jobId: string) => Promise<ProvaAccessoStato>;
}) {
  const predefinita = utenze.find((u) => u.predefinita) ?? null;
  const altre = utenze.filter((u) => !u.predefinita);
  const nelCatalogo = FORNITORI_NEL_CATALOGO.has(fornitore);
  const [nuovaAperta, setNuovaAperta] = useState(false);
  // Le modalità ammesse le dichiara l'adattatore del fornitore, riga per riga: senza
  // righe non si inventa nulla e il form non si apre.
  const modalitaAmmesse = (predefinita ?? altre[0])?.modalita_ammesse ?? [];

  return (
    <SectionCard
      title={fornitore}
      actions={nelCatalogo && modalitaAmmesse.length > 0 ? (
        <Button size="sm" onClick={() => setNuovaAperta((v) => !v)}>Nuova utenza</Button>
      ) : undefined}
    >
      <div id={`utenze-${fornitore}`} className="p-4 space-y-4">
        {!nelCatalogo ? (
          <p className="text-sm text-muted-foreground">
            Per ora la gestisce AIEnergia con la propria utenza
          </p>
        ) : (
          <>
            {nuovaAperta && (
              <FormNuovaUtenza
                fornitore={fornitore}
                modalitaAmmesse={modalitaAmmesse}
                onAnnulla={() => setNuovaAperta(false)}
                onCreata={async () => { setNuovaAperta(false); await onRicarica(); }}
              />
            )}
            <CardPredefinita fornitore={fornitore} utenza={predefinita}
                             onRicarica={onRicarica} onProva={onProva} />
            {altre.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-card/60 px-6 py-10 text-center">
                <KeyRound className="mx-auto size-8 text-muted-foreground/60" />
                <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                  Nessuna utenza oltre la predefinita. Con «Nuova utenza» ne crei una e la assegni a
                  un'agenzia; l'Affiliato inserisce poi le credenziali dal suo profilo.
                </p>
              </div>
            ) : (
              <UtenzeFornitoreTabella utenze={altre} onRicarica={onRicarica} onProva={onProva} />
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * Nuova utenza in catalogo: nome utente e modalità, niente credenziali — quelle le
 * mette l'Affiliato dal suo profilo. Il form compare solo su richiesta
 * (progressive-disclosure) e le opzioni stanno in un fieldset con la sua legenda
 * (regola ux "field-grouping").
 */
function FormNuovaUtenza({ fornitore, modalitaAmmesse, onAnnulla, onCreata }: {
  fornitore: string;
  modalitaAmmesse: string[];
  onAnnulla: () => void;
  onCreata: () => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [modalita, setModalita] = useState(modalitaAmmesse[0] ?? "custodite");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const idU = `nuova-${fornitore}-username`;
  const id = (m: string) => `nuova-${fornitore}-${m}`;

  async function crea(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await adminApi.utenzeCrea({ fornitore, username: username.trim(), modalita, agenzie: [] });
      setUsername("");
      await onCreata();
    } catch (e2) {
      setErr(messaggioErrore(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={crea} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="text-sm font-medium text-foreground">Fornitore</span>
          <p className="h-9 flex items-center text-sm text-foreground font-medium">{fornitore}</p>
        </div>
        <div className="space-y-1">
          <label htmlFor={idU} className="text-sm font-medium text-foreground">Nome utente</label>
          <Input id={idU} value={username} autoComplete="off" required
                 onChange={(e) => setUsername(e.target.value)} />
        </div>
      </div>

      <fieldset className="space-y-2" disabled={busy}>
        <legend className="text-sm font-medium text-foreground mb-1">Come il portale accede ad A2A</legend>
        {modalitaAmmesse.map((m) => (
          <div key={m} className="flex items-start gap-2">
            <input type="radio" id={id(m)} name={id("modalita")} value={m}
                   checked={modalita === m} onChange={() => setModalita(m)}
                   className="mt-1 shrink-0 accent-primary" />
            <label htmlFor={id(m)} className="text-sm text-foreground cursor-pointer">
              {RIGA_MODALITA[m] ?? m}
            </label>
          </div>
        ))}
      </fieldset>

      {err && <p role="alert" className="text-sm text-destructive">{err}</p>}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" type="button" disabled={busy} onClick={onAnnulla}>
          Annulla
        </Button>
        <Button size="sm" type="submit" disabled={busy || username.trim().length === 0}>Crea</Button>
      </div>
    </form>
  );
}

/**
 * Le agenzie che lavorano con l'utenza di casa. Sulla predefinita i collegamenti sono
 * espliciti come su ogni altra utenza: senza il suo nome in questo elenco un'agenzia
 * non manda pratiche. Stessi chip e stesso pannello di ricerca della tabella.
 */
function AgenzieDellaPredefinita({ utenza, onRicarica }: {
  utenza: UtenzaCatalogo;
  onRicarica: () => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  const [daTogliere, setDaTogliere] = useState<{ acc: string; nome: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function togli() {
    if (!daTogliere) return;
    setBusy(true);
    setErr(null);
    try {
      await adminApi.utenzeScollegaAgenzia(utenza.id, daTogliere.acc);
      setDaTogliere(null);
      await onRicarica();
    } catch {
      setErr("Non si è potuto scollegare l'agenzia.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-sm font-medium text-foreground">
          Agenzie assegnate ({utenza.agenzie.length})
        </h5>
        <Button size="sm" variant="outline" onClick={() => setAperto((v) => !v)}>Assegna agenzia</Button>
      </div>
      <ChipAgenzie agenzie={utenza.agenzie}
                   onRimuovi={(acc, nome) => { setErr(null); setDaTogliere({ acc, nome }); }} />
      {aperto && (
        <FormAssegna utenza={utenza} onFatto={async () => { setAperto(false); await onRicarica(); }} />
      )}
      <ConfirmDialog
        open={daTogliere !== null}
        title="Scollega agenzia"
        message={`${daTogliere?.nome ?? ""} resterà senza utenza ${utenza.fornitore}: le sue pratiche si fermano. Confermi?`}
        confirmLabel="Scollega"
        busy={busy}
        error={err}
        onConfirm={togli}
        onCancel={() => setDaTogliere(null)}
      />
    </div>
  );
}

function CardPredefinita({ fornitore, utenza, onRicarica, onProva }: {
  fornitore: string;
  utenza: UtenzaCatalogo | null;
  onRicarica: () => Promise<void>;
  onProva: (jobId: string) => Promise<ProvaAccessoStato>;
}) {
  const [aperto, setAperto] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [chiave, setChiave] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { job_id } = await adminApi.utenzePredefinita(fornitore, {
        username: username.trim(), password, totp_secret: chiave.trim(),
      });
      // Il form si chiude solo quando l'accesso è riuscito davvero: se la verifica è
      // stata rifiutata o non ha risposto, le credenziali restano sotto gli occhi di
      // chi le ha scritte, pronte da correggere.
      const st = await onProva(job_id);
      if (st.stato === "ok") {
        setAperto(false);
        setPassword("");
        setChiave("");
      } else {
        setErr(st.dettaglio || "Verifica non riuscita: controlla nome utente, password e chiave dell'app di autenticazione.");
      }
    } catch {
      setErr("Salvataggio non riuscito: controlla nome utente, password e chiave dell'app di autenticazione.");
    } finally {
      setBusy(false);
    }
  }

  const idU = `pred-${fornitore}-username`;
  const idP = `pred-${fornitore}-password`;
  const idS = `pred-${fornitore}-chiave`;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-foreground">Utenza predefinita</h4>
          {utenza ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <span className="font-medium text-foreground">{utenza.username}</span>
              <StatoUtenza stato={utenza.stato} motivo={utenza.motivo_stato} />
              <span className="text-xs text-muted-foreground tabular-nums">
                Ultimo accesso {formatDateTime(utenza.ultimo_accesso_ok_at)}
              </span>
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-muted-foreground">Nessuna credenziale registrata.</p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setAperto((v) => !v)}>
          Aggiorna credenziali
        </Button>
      </div>

      {utenza && <AgenzieDellaPredefinita utenza={utenza} onRicarica={onRicarica} />}

      {aperto && (
        <form onSubmit={salva} className="mt-4 space-y-3 border-t border-border pt-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label htmlFor={idU} className="text-sm font-medium text-foreground">Nome utente</label>
              <Input id={idU} value={username} autoComplete="off" required
                     onChange={(e) => setUsername(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor={idP} className="text-sm font-medium text-foreground">Password</label>
              <Input id={idP} type="password" value={password} autoComplete="new-password" required
                     onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor={idS} className="text-sm font-medium text-foreground">Chiave dell'app di autenticazione</label>
              <Input id={idS} value={chiave} autoComplete="off" required aria-describedby={`${idS}-help`}
                     onChange={(e) => setChiave(e.target.value)} />
            </div>
          </div>
          <p id={`${idS}-help`} className="text-xs text-muted-foreground">
            È il codice che A2A mostra accanto al QR quando registri l'app. Va bene anche l'indirizzo che
            inizia con otpauth://
          </p>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" type="button" disabled={busy} onClick={() => setAperto(false)}>
              Annulla
            </Button>
            <Button size="sm" type="submit" disabled={busy}>
              {busy ? "Verifica…" : "Salva e verifica"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}


function TettoBrowser({ valore }: { valore: number }) {
  const [n, setN] = useState(String(valore));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function salva() {
    setBusy(true);
    setMsg(null);
    try {
      const out = await adminApi.utenzeImpostazioni(Number(n));
      setN(String(out.sessioni_browser_max));
      setMsg("Impostazione salvata.");
    } catch {
      setMsg("Salvataggio non riuscito: il valore ammesso va da 1 a 6.");
    } finally {
      setBusy(false);
    }
  }

  // Niente intestazione di sezione: ripeterebbe parola per parola l'etichetta del campo.
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="space-y-1">
          <label htmlFor="sessioni-browser-max" className="text-sm font-medium text-foreground">
            Sessioni A2A aperte insieme (massimo)
          </label>
          <Input id="sessioni-browser-max" type="number" min={1} max={6} className="w-24 tabular-nums"
                 value={n} onChange={(e) => setN(e.target.value)} />
        </div>
        <Button size="sm" disabled={busy} onClick={salva}>Salva</Button>
        {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
      </div>
      <p className="text-xs text-muted-foreground">
        Quante finestre del portale fornitore si possono tenere aperte insieme. Da 1 a 6.
      </p>
    </section>
  );
}

function Registro({ eventi }: { eventi: UtenzaEvento[] }) {
  return (
    <SectionCard title="Registro">
      {eventi.length === 0 ? (
        <p className="text-sm text-muted-foreground px-4 py-6">Nessun evento registrato.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-4 font-medium">Data</th>
                <th className="text-left py-2.5 px-4 font-medium">Fornitore</th>
                <th className="text-left py-2.5 px-4 font-medium">Utenza</th>
                <th className="text-left py-2.5 px-4 font-medium">Tipo</th>
                <th className="text-left py-2.5 px-4 font-medium">Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {eventi.map((e) => (
                <tr key={e.id} className="border-t border-border align-top">
                  <td className="py-2.5 px-4 text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatDateTime(e.at)}
                  </td>
                  <td className="py-2.5 px-4 text-muted-foreground">{e.fornitore}</td>
                  <td className="py-2.5 px-4 text-foreground">{e.username}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{e.tipo.replace(/_/g, " ")}</td>
                  <td className="py-2.5 px-4 text-muted-foreground break-words">{e.dettaglio || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
